/**
 * Small pieces shared by the account screens (sign-in, subscription, onboarding) and the MCPs, Theme and
 * Account settings categories: the Pro mark, the signed-in state, saving a JSON file, clipboard copy,
 * prices and dates. Imports the area's stylesheet (web.md "Extension points": each area imports its own).
 */

import './account.css';
import type { BillingPeriod, Entitlement } from '@digester/core';
import { showToast } from '../app/Toast.js';
import { useEntitlement, useLocalSettings } from '../app/hooks.js';

/** The small "Pro" badge on areas a Free plan can see but not use (web.md §10). */
export function ProMark({ title = 'Included in Pro' }: { title?: string }) {
  return (
    <b className="pro-mark" title={title}>
      Pro
    </b>
  );
}

export interface AccountState {
  /** False until local settings have loaded. */
  ready: boolean;
  signedIn: boolean;
  email: string | null;
  entitlement: Entitlement | undefined;
}

export function useAccountState(): AccountState {
  const local = useLocalSettings();
  const entitlement = useEntitlement();
  const email = local?.account?.email ?? null;
  return { ready: local !== undefined && entitlement !== undefined, signedIn: email !== null, email, entitlement };
}

/** Download `value` as a pretty-printed JSON file (the prototype's `downloadWorkspace`). */
export function saveJsonFile(fileName: string, value: unknown): void {
  const blob = new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Read a user-chosen text file, refusing anything larger than `maxBytes`. */
export async function readTextFile(file: File, maxBytes: number): Promise<string> {
  if (file.size > maxBytes) throw new Error('The file is too large');
  return file.text();
}

/** Copy text; a toast confirms. Without clipboard access the text is selected for a manual copy. */
export async function copyText(text: string, what: string, fallback?: HTMLInputElement | HTMLTextAreaElement | null): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    showToast(`${what} copied`);
  } catch {
    if (fallback) {
      fallback.focus();
      fallback.select();
    }
    showToast('Select the text and copy it');
  }
}

/** 1198 → "11.98 EUR". */
export function formatPrice(cents: number, currency = 'EUR'): string {
  return `${(cents / 100).toFixed(2)} ${currency}`;
}

export function periodLabel(period: BillingPeriod | null): string {
  if (period === 'month') return 'Monthly';
  if (period === 'year') return 'Yearly';
  return '—';
}

const dateFormat = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const timeFormat = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export function formatDate(ms: number | null | undefined): string {
  return typeof ms === 'number' && Number.isFinite(ms) ? dateFormat.format(ms) : '—';
}

export function formatDateTime(ms: number | null | undefined): string {
  return typeof ms === 'number' && Number.isFinite(ms) ? timeFormat.format(ms) : 'Never';
}

/** 1536 → "1.5 KB". */
export function formatBytes(bytes: number | null | undefined): string {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes)) return 'Unknown';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value >= 100 ? Math.round(value) : value.toFixed(1)} ${units[unit] ?? 'GB'}`;
}
